import { RevenueLog } from '@/hooks/useSupabaseStore';
import { useMemo } from 'react';

interface Props {
  logs: RevenueLog[];
}

export default function RevenueChart({ logs }: Props) {
  const months = useMemo(() => {
    const map: Record<string, { month: string; revenue: number; newLoans: number; redeemedLoans: number }> = {};
    const now = new Date();
    // Generate last 6 months buckets
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `T${d.getMonth() + 1}/${d.getFullYear()}`;
      map[key] = { month: label, revenue: 0, newLoans: 0, redeemedLoans: 0 };
    }

    logs.forEach((log) => {
      const ym = log.createdAt.slice(0, 7); // YYYY-MM
      if (map[ym]) {
        map[ym].revenue += log.interestAmount;
        if (log.type === 'redeem') map[ym].redeemedLoans += 1;
        else map[ym].newLoans += 1; // extend counts as active loan activity
      }
    });

    return Object.values(map);
  }, [logs]);

  const maxRevenue = Math.max(...months.map((m) => m.revenue), 1);
  const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
  const bestMonth = months.reduce((best, m) => (m.revenue > best.revenue ? m : best), months[0]);

  return (
    <div className="bg-white rounded-2xl p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-gray-800 text-base">Doanh Thu 6 Tháng Gần Đây</h3>
        <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">Thực tế từ giao dịch</span>
      </div>
      <div className="flex items-end gap-3 h-36">
        {months.map((m) => {
          const heightPct = (m.revenue / maxRevenue) * 100;
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="relative w-full flex items-end justify-center" style={{ height: '120px' }}>
                <div
                  className="w-full bg-gradient-to-t from-[#f59e0b] to-[#fbbf24] rounded-t-lg transition-all duration-500 group-hover:from-[#d97706] group-hover:to-[#f59e0b] cursor-pointer"
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                  title={`${m.revenue.toLocaleString('vi-VN')}đ`}
                ></div>
              </div>
              <span className="text-gray-400 text-[10px] whitespace-nowrap">{m.month}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3">
        <div>
          <p className="text-gray-400 text-xs">Tháng cao nhất</p>
          <p className="text-gray-800 font-bold text-sm">{bestMonth ? bestMonth.month : '—'}</p>
          <p className="text-gray-600 text-xs">{bestMonth ? bestMonth.revenue.toLocaleString('vi-VN') : '0'}đ</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs">Tổng 6 tháng</p>
          <p className="text-gray-800 font-bold text-sm">{totalRevenue.toLocaleString('vi-VN')}đ</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs">Số giao dịch</p>
          <p className="text-emerald-600 font-bold text-sm">{logs.length} giao dịch</p>
        </div>
      </div>
    </div>
  );
}